import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  User, Zap, Wrench, CreditCard, ArrowLeft, Loader2,
  ChevronRight, Plus, Trash2, Copy, Camera
} from "lucide-react";
import { INDIA_STATES } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Charger segment config (same pattern as Customer Request form) ────────────
const CHARGER_SEGMENTS = [
  { label: "Small Charger",    icon: "🔋" },
  { label: "Fast Charger",     icon: "⚡" },
  { label: "Rickshaw Charger", icon: "🛺" },
];

const SEGMENT_MODELS = {
  "Small Charger":    ["54.6V / 6A","58.4V / 6A","67.2V / 6A","69.3V / 6A","71.2V / 6A","73V / 6A","84V / 5A"],
  "Fast Charger":     ["54.6V / 10A","58.4V / 10A","67.2V / 10A","68.4V / 10A","69.3V / 10A","71.2V / 10A","73V / 10A","84V / 10A"],
  "Rickshaw Charger": ["54.6V / 15A","58.4V / 15A","67.2V / 15A","69.3V / 15A","71.2V / 15A","73V / 15A","54.6V / 20A","58.4V / 20A","67.2V / 20A","69.3V / 20A","71.2V / 20A","73V / 20A"],
};

const BODY_TYPES = ["Metal Body","Aluminium Body"];
const WARRANTY_STATUSES = ["XP Charger","Under Warranty","Out of Warranty","Expired","Unknown"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 12 }, (_, i) => String(currentYear - i));

const EMPTY_CHARGER = { charger_type:"", charger_spec:"", brand_model:"", serial_number:"", body_type:"", warranty_status:"", mfg_month:"", mfg_year:"" };

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-9 h-9 rounded-xl bg-green-600 flex items-center justify-center flex-shrink-0">
      <Icon size={16} className="text-white" />
    </div>
    <div>
      <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>{title}</h3>
      {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
    </div>
  </div>
);

const F = ({ label, required, error, children }) => (
  <div>
    <Label className="text-xs font-medium text-slate-700">
      {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
    {error && <p className="text-xs text-rose-500 mt-1">{error}</p>}
  </div>
);

export default function SCEntry() {
  const navigate = useNavigate();
  const location = useLocation();
  const phoneTimer = useRef(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Customer
  const [custName, setCustName] = useState("");
  const [phone, setPhone] = useState("");
  const [altPhone, setAltPhone] = useState("");
  const [address, setAddress] = useState("");
  const [landmark, setLandmark] = useState("");
  const [city, setCity] = useState("");
  const [custState, setCustState] = useState("");
  const [pincode, setPincode] = useState("");

  // Chargers
  const [chargers, setChargers] = useState([{ ...EMPTY_CHARGER }]);

  const handlePhone = (val) => setPhone(val);

  // Pre-fill from navigation state (e.g. "SC Job" button on customer request row)
  const [prefillBanner, setPrefillBanner] = useState("");
  useEffect(() => {
    const pf = location.state?.prefill;
    if (!pf) return;
    const c = pf.customer || {};
    if (c.name)      setCustName(c.name);
    if (c.phone)     setPhone(c.phone);
    if (c.alt_phone) setAltPhone(c.alt_phone);
    if (c.address)   setAddress(c.address);
    if (c.landmark)  setLandmark(c.landmark);
    if (c.city)      setCity(c.city);
    if (c.state)     setCustState(c.state);
    if (c.pincode)   setPincode(c.pincode);
    if (pf.chargers?.length) setChargers(pf.chargers.map(ch => ({ ...EMPTY_CHARGER, ...ch })));
    if (pf.from_job_no) setPrefillBanner(`Pre-filled from customer request ${pf.from_job_no} — verify all details before saving.`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addCharger = () => setChargers(p => [...p, { ...EMPTY_CHARGER }]);
  const removeCharger = (i) => setChargers(p => p.filter((_, idx) => idx !== i));
  const duplicateCharger = (i) => setChargers(p => { const c = { ...p[i] }; const n = [...p]; n.splice(i + 1, 0, c); return n; });
  const updateCharger = (i, field, val) => setChargers(p => { const n = [...p]; n[i] = { ...n[i], [field]: val }; return n; });

  // Photo upload per charger
  const [photoUploading, setPhotoUploading] = useState({});
  const handleChargerPhoto = async (i, file) => {
    if (!file) return;
    const MAX = 20 * 1024 * 1024;
    if (file.size > MAX) { toast.error(`Photo too large — max 20 MB (yours: ${(file.size/1024/1024).toFixed(1)} MB)`); return; }
    setPhotoUploading(p => ({ ...p, [i]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateCharger(i, "photo_url", r.data.url);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Photo upload failed"); }
    finally { setPhotoUploading(p => ({ ...p, [i]: false })); }
  };

  // Fault & Notes
  const [faultDesc, setFaultDesc] = useState("");
  const [techNotes, setTechNotes] = useState("");
  const [repairNotes, setRepairNotes] = useState("");

  // Payment
  const [totalAmt, setTotalAmt] = useState("");
  const [paidAmt, setPaidAmt] = useState("");
  const [payMethod, setPayMethod] = useState("Cash");
  const due = Math.max(0, parseFloat(totalAmt || 0) - parseFloat(paidAmt || 0));

  const validate = () => {
    const e = {};
    if (!custName.trim()) e.name = "Required";
    if (!phone.trim()) e.phone = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await api.post(`${API}/sc/jobs`, {
        customer: { name: custName.trim(), phone: phone.trim(), alt_phone: altPhone.trim(), address: address.trim(), landmark: landmark.trim(), city: city.trim(), state: custState, pincode: pincode.trim() },
        chargers,
        specs: { fault_description: faultDesc, technical_notes: techNotes, repair_notes: repairNotes },
        payment: { amount: parseFloat(totalAmt) || 0, paid_amount: parseFloat(paidAmt) || 0, method: payMethod },
      });
      toast.success(`SC Job ${res.data.sc_no} created!`);
      navigate(`/sc/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create SC job");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#f5faf6]">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200/80 shadow-sm">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
          </Button>
          <img src="/logo.jpg" alt="XSTRONG POWER" className="h-8 w-auto object-contain flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm text-slate-900 leading-tight truncate" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
              New Service Job
            </p>
            <p className="text-xs text-green-600 font-medium leading-tight">XSTRONG POWER — EV Charger Service</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

      {/* Pre-fill banner */}
      {prefillBanner && (
        <div className="flex items-start gap-3 mb-5 p-3.5 bg-amber-50 border border-amber-200 rounded-2xl">
          <span className="text-amber-500 text-base flex-shrink-0 mt-0.5">⚡</span>
          <p className="text-sm text-amber-800 font-medium">{prefillBanner}</p>
          <button onClick={() => setPrefillBanner("")} className="ml-auto text-amber-400 hover:text-amber-600 flex-shrink-0">
            <span className="text-lg leading-none">×</span>
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Customer Details */}
        <Card className="rounded-2xl border border-slate-200/70 bg-white">
          <CardContent className="p-5">
            <SectionHeader icon={User} title="Customer Details" subtitle="Who is bringing the charger?" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <F label="Customer Name" required error={errors.name}>
                <Input placeholder="Full name" value={custName} onChange={e => setCustName(e.target.value)}
                  className={`h-10 text-sm ${errors.name ? "border-rose-400" : ""}`} data-testid="sc-cust-name" />
              </F>
              <F label="Phone Number" required error={errors.phone}>
                <Input placeholder="Mobile number" value={phone} onChange={e => handlePhone(e.target.value)}
                  className={`h-10 text-sm ${errors.phone ? "border-rose-400" : ""}`} data-testid="sc-cust-phone" />
              </F>
              <F label="Alternate Number">
                <Input placeholder="Alt contact (optional)" value={altPhone} onChange={e => setAltPhone(e.target.value)} className="h-10 text-sm" />
              </F>
            </div>

            {/* Address section */}
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Customer Address</p>
              <F label="Street Address">
                <Input placeholder="House / Shop No., Street, Area"
                  value={address} onChange={e => setAddress(e.target.value)} className="h-10 text-sm" />
              </F>
              <F label="Landmark">
                <Input placeholder="e.g. Near Metro Station, Opposite City Mall"
                  value={landmark} onChange={e => setLandmark(e.target.value)} className="h-10 text-sm" />
              </F>
              <div className="grid grid-cols-3 gap-3">
                <F label="City">
                  <Input placeholder="e.g. Mumbai"
                    value={city} onChange={e => setCity(e.target.value)} className="h-10 text-sm" />
                </F>
                <F label="State">
                  <Select value={custState} onValueChange={setCustState}>
                    <SelectTrigger className="h-10 text-sm"><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {INDIA_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="PIN Code">
                  <Input placeholder="400001" value={pincode} onChange={e => setPincode(e.target.value)}
                    className="h-10 text-sm font-mono" maxLength={6} inputMode="numeric" />
                </F>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Charger Details — per charger card */}
        <div className="space-y-3">
          {chargers.map((c, i) => (
            <Card key={i} className="rounded-2xl border border-slate-200/70 bg-white">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-green-600 flex items-center justify-center flex-shrink-0">
                      <Zap size={16} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                        Charger Details{chargers.length > 1 ? ` #${i + 1}` : ""}
                      </h3>
                      <p className="text-xs text-slate-400">Device information</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {chargers.length > 1 && (
                      <button type="button" onClick={() => duplicateCharger(i)} title="Duplicate"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-green-600 hover:bg-green-50 transition-colors">
                        <Copy size={14} />
                      </button>
                    )}
                    {chargers.length > 1 && (
                      <button type="button" onClick={() => removeCharger(i)} title="Remove"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* ── Charger Type Segment ─────────────────────────────── */}
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-medium text-slate-700">
                      Charger Type <span className="text-rose-500">*</span>
                    </Label>
                    <div className="flex gap-2 mt-1.5">
                      {CHARGER_SEGMENTS.map(seg => (
                        <button
                          key={seg.label}
                          type="button"
                          onClick={() => {
                            updateCharger(i, "charger_type", seg.label);
                            updateCharger(i, "charger_spec", "");
                          }}
                          className={`flex-1 py-2.5 px-2 rounded-xl text-xs font-semibold border transition-all leading-tight ${
                            c.charger_type === seg.label
                              ? "bg-green-600 text-white border-green-600 shadow-sm"
                              : "bg-white text-slate-600 border-slate-200 hover:border-green-400 hover:text-green-700 hover:bg-green-50"
                          }`}
                        >
                          <span className="block text-base mb-0.5">{seg.icon}</span>
                          {seg.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* ── Model dropdown — only after segment is picked ──────── */}
                  {c.charger_type && (
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-medium text-slate-700">
                        Select Model <span className="text-rose-500">*</span>
                      </Label>
                      <Select value={c.charger_spec} onValueChange={v => updateCharger(i, "charger_spec", v)}>
                        <SelectTrigger className="mt-1.5 h-10 text-sm font-semibold">
                          <SelectValue placeholder={`Select ${c.charger_type} model…`} />
                        </SelectTrigger>
                        <SelectContent>
                          {(SEGMENT_MODELS[c.charger_type] || []).map(m => (
                            <SelectItem key={m} value={m}>
                              <span className="font-mono font-semibold text-green-700">{m}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {c.charger_spec && (
                        <p className="text-xs text-slate-500 mt-1">
                          Selected: <span className="font-semibold text-green-700">{c.charger_type} — {c.charger_spec}</span>
                        </p>
                      )}
                    </div>
                  )}

                  <F label="Serial Number">
                    <Input placeholder="Charger serial number" value={c.serial_number}
                      onChange={e => updateCharger(i, "serial_number", e.target.value)} className="h-10 text-sm font-mono" />
                  </F>
                  <F label="Body Type">
                    <Select value={c.body_type} onValueChange={v => updateCharger(i, "body_type", v)}>
                      <SelectTrigger className="h-10 text-sm"><SelectValue placeholder="Select body type" /></SelectTrigger>
                      <SelectContent>{BODY_TYPES.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Warranty Status">
                    <Select value={c.warranty_status} onValueChange={v => updateCharger(i, "warranty_status", v)}>
                      <SelectTrigger className="h-10 text-sm"><SelectValue placeholder="Select warranty status" /></SelectTrigger>
                      <SelectContent>{WARRANTY_STATUSES.map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Manufacturing Month">
                    <Select value={c.mfg_month} onValueChange={v => updateCharger(i, "mfg_month", v)}>
                      <SelectTrigger className="h-10 text-sm"><SelectValue placeholder="Month" /></SelectTrigger>
                      <SelectContent className="max-h-52">
                        {MONTHS.map((m, idx) => <SelectItem key={idx} value={String(idx + 1)}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Manufacturing Year">
                    <Select value={c.mfg_year} onValueChange={v => updateCharger(i, "mfg_year", v)}>
                      <SelectTrigger className="h-10 text-sm"><SelectValue placeholder="Year" /></SelectTrigger>
                      <SelectContent className="max-h-52">
                        {YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </F>

                  {/* Photo upload */}
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-medium text-slate-700">Charger Photo <span className="text-slate-400 font-normal">(optional, max 20 MB)</span></Label>
                    <div className="mt-1.5 flex items-start gap-3 flex-wrap">
                      {c.photo_url ? (
                        <div className="relative group">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} alt="Charger"
                            className="w-24 h-24 object-cover rounded-xl border-2 border-green-200 shadow-sm" />
                          <button type="button" onClick={() => updateCharger(i, "photo_url", "")}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-rose-500 text-white rounded-full text-xs font-bold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                        </div>
                      ) : null}
                      <label className="cursor-pointer">
                        <input type="file" accept="image/*" className="hidden"
                          onChange={e => handleChargerPhoto(i, e.target.files?.[0])} />
                        <div className={`h-10 px-4 flex items-center gap-2 rounded-xl border text-sm transition-colors ${
                          photoUploading[i]
                            ? "border-green-300 bg-green-50 text-green-600"
                            : "border-slate-200 bg-white text-slate-600 hover:border-green-400 hover:text-green-700"
                        }`}>
                          {photoUploading[i]
                            ? <><Loader2 size={14} className="animate-spin" /> Uploading…</>
                            : <><Camera size={14} /> {c.photo_url ? "Change Photo" : "Upload Photo"}</>}
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Add Charger */}
          <button type="button" onClick={addCharger}
            className="w-full flex items-center justify-center gap-2 text-sm text-green-700 hover:text-green-800 border border-dashed border-green-300 hover:border-green-500 rounded-2xl py-3.5 transition-colors bg-green-50/30 hover:bg-green-50">
            <Plus size={15} /> Add Another Charger
          </button>
        </div>

        {/* Fault & Notes */}
        <Card className="rounded-2xl border border-slate-200/70 bg-white">
          <CardContent className="p-5">
            <SectionHeader icon={Wrench} title="Fault & Notes" subtitle="What is the problem?" />
            <div className="space-y-3">
              <F label="Fault Description">
                <Textarea placeholder="Describe the fault reported by customer..."
                  value={faultDesc} onChange={e => setFaultDesc(e.target.value)} className="text-sm resize-none" rows={2} />
              </F>
              <F label="Technical Notes">
                <Textarea placeholder="Technical observations (for technician)..."
                  value={techNotes} onChange={e => setTechNotes(e.target.value)} className="text-sm resize-none" rows={2} />
              </F>
              <F label="Repair Notes">
                <Textarea placeholder="What was repaired / replaced..."
                  value={repairNotes} onChange={e => setRepairNotes(e.target.value)} className="text-sm resize-none" rows={2} />
              </F>
            </div>
          </CardContent>
        </Card>

        {/* Payment */}
        <Card className="rounded-2xl border border-slate-200/70 bg-white">
          <CardContent className="p-5">
            <SectionHeader icon={CreditCard} title="Payment" subtitle="Billing information" />
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <F label="Total Amount (₹)">
                  <Input type="number" min="0" placeholder="0.00" value={totalAmt}
                    onChange={e => setTotalAmt(e.target.value)} className="h-10 text-sm tabular-nums" data-testid="sc-total-amt" />
                </F>
                <F label="Amount Paid (₹)">
                  <Input type="number" min="0" placeholder="0.00" value={paidAmt}
                    onChange={e => setPaidAmt(e.target.value)} className="h-10 text-sm tabular-nums" />
                </F>
              </div>
              <F label="Payment Method">
                <Select value={payMethod} onValueChange={setPayMethod}>
                  <SelectTrigger className="h-10 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Cash","UPI","Card","Bank Transfer"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              {parseFloat(totalAmt) > 0 && (
                <div className="space-y-1 p-3 bg-slate-50 rounded-xl">
                  <div className="flex justify-between text-xs text-slate-600">
                    <span>Total</span><span className="font-medium tabular-nums">₹{parseFloat(totalAmt || 0).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between text-xs text-emerald-700">
                    <span>Paid</span><span className="font-medium tabular-nums">₹{parseFloat(paidAmt || 0).toLocaleString("en-IN")}</span>
                  </div>
                  <Separator className="my-1" />
                  <div className="flex justify-between text-xs font-semibold text-rose-600">
                    <span>Due</span><span className="tabular-nums">₹{due.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex items-center justify-end gap-3 pb-6">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={loading}>Cancel</Button>
          <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white min-w-[180px]"
            disabled={loading} data-testid="sc-submit">
            {loading
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
              : <><ChevronRight className="w-4 h-4 mr-1" />Create Service Job</>}
          </Button>
        </div>
      </form>
      </div>
    </div>
  );
}
